import { ArrowUpRight, CircleAlert, Clock3, ListTodo, Plus, Target } from 'lucide-react'
import { useMemo } from 'react'
import { formatDate } from '@/utils/format'
import type { Task } from '@/lib/types'

interface TaskDashboardHeaderProps {
  tasks: Task[]
  onCreateTask: () => void
}

export function TaskDashboardHeader({ tasks, onCreateTask }: TaskDashboardHeaderProps) {
  const overview = useMemo(() => {
    const now = new Date()
    const today = formatDate(now)
    const todayTasks = tasks.filter((task) => task.dueDate === today)
    const completedToday = todayTasks.filter((task) => task.status === 'completed').length
    const pendingToday = todayTasks.length - completedToday
    const overdue = tasks.filter(
      (task) => task.status === 'pending' && task.dueDate && task.dueDate < today,
    ).length
    const totalPending = tasks.filter((task) => task.status === 'pending').length
    const progress = todayTasks.length > 0
      ? Math.round((completedToday / todayTasks.length) * 100)
      : 0
    const dateLabel = new Intl.DateTimeFormat('ko-KR', {
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    }).format(now)
    const hour = now.getHours()
    const greeting = hour < 5
      ? '고요한 시간이에요'
      : hour < 12
        ? '좋은 아침이에요'
        : hour < 18
          ? '좋은 오후예요'
          : '오늘도 수고했어요'

    return {
      completedToday,
      pendingToday,
      overdue,
      totalPending,
      progress,
      dateLabel,
      greeting,
      todayTotal: todayTasks.length,
    }
  }, [tasks])

  const progressMessage = overview.todayTotal > 0
    ? `${overview.todayTotal}개 중 ${overview.completedToday}개를 완료했어요`
    : '오늘 마감 작업이 없습니다. 여유롭게 다음 일을 준비해보세요'

  const stats = [
    {
      label: '오늘 남은 작업',
      value: overview.pendingToday,
      icon: Clock3,
      tone: 'text-primary-600 dark:text-primary-400 bg-primary-100/80 dark:bg-primary-900/25',
    },
    {
      label: '기한이 지난 작업',
      value: overview.overdue,
      icon: CircleAlert,
      tone: overview.overdue > 0
        ? 'text-danger-600 dark:text-red-400 bg-danger-50 dark:bg-red-950/20'
        : 'text-success-600 dark:text-emerald-400 bg-success-50 dark:bg-emerald-950/20',
    },
    {
      label: '전체 대기 작업',
      value: overview.totalPending,
      icon: ListTodo,
      tone: 'text-violet-600 dark:text-violet-400 bg-violet-100/80 dark:bg-violet-900/25',
    },
  ]

  return (
    <section className="workspace-hero mb-5 p-4 sm:p-5 lg:p-6" aria-labelledby="task-dashboard-title">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[var(--workspace-border)] bg-[var(--workspace-panel-strong)] px-3 py-1.5 text-meta font-semibold tracking-[0.08em] text-accent-primary shadow-e1">
            <Target className="h-3.5 w-3.5" aria-hidden="true" />
            오늘의 워크스페이스
          </div>
          <h1
            id="task-dashboard-title"
            className="max-w-3xl text-[clamp(1.55rem,3vw,2.4rem)] font-bold leading-[1.15] tracking-[-0.04em] text-heading"
          >
            {overview.greeting}
            <span className="block bg-gradient-to-r from-primary-600 via-primary-500 to-violet-500 bg-clip-text text-transparent">
              가장 중요한 일부터 가볍게.
            </span>
          </h1>
          <p className="mt-3 text-sm text-caption sm:text-base">
            <span className="font-medium text-body">{overview.dateLabel}</span>
            <span className="mx-2 text-disabled" aria-hidden="true">·</span>
            {progressMessage}
          </p>
        </div>

        <div className="flex items-center justify-between gap-4 lg:justify-end">
          <div
            className="relative grid h-[82px] w-[82px] flex-shrink-0 place-items-center rounded-full p-[7px] shadow-e1"
            style={{
              background: `conic-gradient(var(--color-primary-500) ${overview.progress}%, var(--surface-muted) ${overview.progress}% 100%)`,
            }}
            role="progressbar"
            aria-label="오늘 작업 완료율"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={overview.progress}
          >
            <div className="grid h-full w-full place-items-center rounded-full bg-[var(--workspace-panel-strong)]">
              <div className="text-center">
                <strong className="block text-lg leading-none tabular-nums text-heading">{overview.progress}%</strong>
                <span className="mt-1 block text-meta text-caption">오늘 완료</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onCreateTask}
            className="workspace-primary-action inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-white transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            새 작업
            <ArrowUpRight className="hidden h-4 w-4 sm:block" aria-hidden="true" />
          </button>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-3 gap-2.5 border-t border-[var(--workspace-border)] pt-4 sm:gap-3">
        {stats.map(({ label, value, icon: Icon, tone }) => (
          <div
            key={label}
            className="min-w-0 rounded-2xl border border-[var(--workspace-border)] bg-[var(--workspace-panel)] p-2.5 shadow-[inset_0_1px_0_var(--workspace-highlight)] sm:flex sm:items-center sm:gap-3 sm:p-3"
          >
            <span className={`mb-2 flex h-8 w-8 items-center justify-center rounded-xl sm:mb-0 sm:h-9 sm:w-9 ${tone}`}>
              <Icon className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <dd className="text-lg font-bold leading-none tabular-nums text-heading sm:text-xl">{value}</dd>
              <dt className="mt-1 truncate text-meta text-caption">{label}</dt>
            </div>
          </div>
        ))}
      </dl>
    </section>
  )
}
